import { useState } from 'react';
import type { CalendarEventRead, CampaignRead, PlannedPostRead } from '../../types/api';
import {
  buildMonthGrid,
  dayLabel,
  WEEKDAY_LABELS,
  WEEKDAY_NARROW_LABELS,
  type CalendarDay,
} from './calendarDates';
import { EntryChip, PLANNED_POST_DRAG_TYPE } from './EntryChip';

/** How many chips a day shows before it collapses the rest into a count. */
const CHIPS_PER_DAY = 3;

/** How many dots a phone's day shows before a "+". */
const DOTS_PER_DAY = 3;

/** One day's slice of a key date, so a multi-day event reads as one band. */
export interface EventBand {
  event: CalendarEventRead;
  /** First day of the event's range — the day that carries the title. */
  isStart: boolean;
  /** Last day of the range. */
  isEnd: boolean;
}

interface MonthGridProps {
  month: string;
  today: string;
  postsByDay: ReadonlyMap<string, PlannedPostRead[]>;
  bandsByDay: ReadonlyMap<string, EventBand[]>;
  campaignsById: ReadonlyMap<string, CampaignRead>;
  onOpenPost: (post: PlannedPostRead) => void;
  onOpenEvent: (event: CalendarEventRead) => void;
  /** Clicked an empty day: open the quick-add form with that date filled in. */
  onQuickAdd: (iso: string) => void;
  /** "+N more": hand the day to the list view, which has room for all of it. */
  onShowDay: (iso: string) => void;
  /** Dropped a chip on another day. Optimistic — the row moves, then the PUT. */
  onMovePost: (postId: string, iso: string) => void;
  /**
   * `dots` is the phone's grid (ruling 11 of MOBILE_RESPONSIVE_PLAN.md): each
   * day one button, its number and up to three dots, picking the day the
   * agenda under the grid shows. Nothing is dragged or quick-added from it.
   */
  variant?: 'full' | 'dots';
  /** The day the agenda shows, ringed on the dot grid. */
  selectedDay?: string | null;
  onSelectDay?: (iso: string) => void;
}

/** A day's summary for its dot-grid button: the date, then what is on it. */
function dotDayLabel(iso: string, postCount: number, eventCount: number): string {
  const parts: string[] = [];
  if (postCount > 0) parts.push(`${postCount} ${postCount === 1 ? 'post' : 'posts'}`);
  if (eventCount > 0) parts.push(`${eventCount} ${eventCount === 1 ? 'event' : 'events'}`);
  return `${dayLabel(iso)}, ${parts.length > 0 ? parts.join(', ') : 'nothing planned'}`;
}

/** One dot: a class for its kind or state, and a campaign colour when it has one. */
interface DotMark {
  key: string;
  className: string;
  color?: string;
}

interface DotDayProps {
  day: CalendarDay;
  posts: readonly PlannedPostRead[];
  bands: readonly EventBand[];
  campaignsById: ReadonlyMap<string, CampaignRead>;
  isSelected: boolean;
  onSelect: (iso: string) => void;
}

/**
 * One day on the phone's grid. A plan that runs itself is dotted in its state's
 * colour, any other post in its campaign's (machine blue without one), and a
 * key date as an amber ring; past three, a "+".
 */
function DotDay({ day, posts, bands, campaignsById, isSelected, onSelect }: DotDayProps) {
  const marks: DotMark[] = [
    ...bands.map((band) => ({
      key: `event-${band.event.id}`,
      className: 'month-dot month-dot--key',
    })),
    ...posts.map((post) => {
      const campaign =
        post.campaign_id === null ? null : (campaignsById.get(post.campaign_id) ?? null);
      const automated = post.automation !== 'none';
      return {
        key: `post-${post.id}`,
        className: automated ? `month-dot month-dot--${post.automation_status}` : 'month-dot',
        color: !automated && campaign ? campaign.color : undefined,
      };
    }),
  ];
  const shown = marks.slice(0, DOTS_PER_DAY);

  const classes = ['month-grid__day', 'month-grid__day--dots'];
  if (!day.inMonth) classes.push('month-grid__day--muted');
  if (day.isToday) classes.push('month-grid__day--today');
  if (isSelected) classes.push('month-grid__day--selected');

  return (
    <button
      className={classes.join(' ')}
      type="button"
      aria-pressed={isSelected}
      aria-label={dotDayLabel(day.iso, posts.length, bands.length)}
      onClick={() => onSelect(day.iso)}
    >
      <span className="month-grid__dotnum">{Number(day.iso.slice(8))}</span>
      <span className="month-dots" aria-hidden="true">
        {shown.map((mark) => (
          <span
            className={mark.className}
            key={mark.key}
            style={mark.color ? { background: mark.color } : undefined}
          />
        ))}
        {marks.length > shown.length ? <span className="month-dots__more">+</span> : null}
      </span>
    </button>
  );
}

/**
 * The month, Monday first, always whole weeks.
 *
 * Dragging a chip to another day is the fast path for rescheduling; the modal's
 * date field is the same move for anyone on a keyboard or a touch screen, so
 * nothing here is the only way to do anything.
 */
export function MonthGrid({
  month,
  today,
  postsByDay,
  bandsByDay,
  campaignsById,
  onOpenPost,
  onOpenEvent,
  onQuickAdd,
  onShowDay,
  onMovePost,
  variant = 'full',
  selectedDay = null,
  onSelectDay,
}: MonthGridProps) {
  const [dropDay, setDropDay] = useState<string | null>(null);

  // Left unmemoised on purpose — the React Compiler owns memoisation here.
  const days = buildMonthGrid(month, today).flat();

  if (variant === 'dots') {
    return (
      <div className="month-grid month-grid--dots">
        {WEEKDAY_LABELS.map((label, index) => (
          <div className="month-grid__weekday" key={label}>
            <span aria-hidden="true">{WEEKDAY_NARROW_LABELS[index]}</span>
            <span className="visually-hidden">{label}</span>
          </div>
        ))}

        {days.map((day) => (
          <DotDay
            key={day.iso}
            day={day}
            posts={postsByDay.get(day.iso) ?? []}
            bands={bandsByDay.get(day.iso) ?? []}
            campaignsById={campaignsById}
            isSelected={day.iso === selectedDay}
            onSelect={(iso) => onSelectDay?.(iso)}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="month-grid">
      {WEEKDAY_LABELS.map((label) => (
        <div className="month-grid__weekday" key={label}>
          {label}
        </div>
      ))}

      {days.map((day, index) => {
        const posts = postsByDay.get(day.iso) ?? [];
        const bands = bandsByDay.get(day.iso) ?? [];
        const visible = posts.slice(0, CHIPS_PER_DAY);
        const hidden = posts.length - visible.length;
        const isRowStart = index % 7 === 0;

        const classes = ['month-grid__day'];
        if (!day.inMonth) classes.push('month-grid__day--muted');
        if (day.isToday) classes.push('month-grid__day--today');
        if (dropDay === day.iso) classes.push('month-grid__day--drop');

        return (
          <div
            className={classes.join(' ')}
            key={day.iso}
            onDragOver={(event) => {
              if (!event.dataTransfer.types.includes(PLANNED_POST_DRAG_TYPE)) return;
              // Preventing the default is what makes this a drop target at all.
              event.preventDefault();
              event.dataTransfer.dropEffect = 'move';
              if (dropDay !== day.iso) setDropDay(day.iso);
            }}
            onDragLeave={() => {
              setDropDay((current) => (current === day.iso ? null : current));
            }}
            onDrop={(event) => {
              event.preventDefault();
              setDropDay(null);
              const postId = event.dataTransfer.getData(PLANNED_POST_DRAG_TYPE);
              if (postId) onMovePost(postId, day.iso);
            }}
          >
            <div className="month-grid__dayhead">
              <button
                className="month-grid__daynum"
                type="button"
                aria-label={`Plan a post on ${dayLabel(day.iso)}`}
                onClick={() => onQuickAdd(day.iso)}
              >
                {Number(day.iso.slice(8))}
              </button>
            </div>

            {bands.map((band) => {
              const bandClasses = ['event-band'];
              if (band.isStart) bandClasses.push('event-band--start');
              if (band.isEnd) bandClasses.push('event-band--end');
              // A band that started earlier in the week keeps its name on the
              // first cell of each row, so a run of days is still legible.
              const showsTitle = band.isStart || isRowStart;

              return (
                <button
                  className={bandClasses.join(' ')}
                  key={band.event.id}
                  type="button"
                  aria-label={`Edit event ${band.event.title}`}
                  title={band.event.title}
                  onClick={() => onOpenEvent(band.event)}
                >
                  {showsTitle ? band.event.title : ''}
                </button>
              );
            })}

            <div className="month-grid__entries">
              {visible.map((post) => (
                <EntryChip
                  key={post.id}
                  post={post}
                  campaign={
                    post.campaign_id === null ? null : (campaignsById.get(post.campaign_id) ?? null)
                  }
                  onOpen={onOpenPost}
                />
              ))}

              {hidden > 0 ? (
                <button
                  className="month-grid__more"
                  type="button"
                  onClick={() => onShowDay(day.iso)}
                >
                  +{hidden} more
                </button>
              ) : null}
            </div>

            {/* Mouse shortcut only: the day number above is the same action,
                and it is the one in the tab order. */}
            <div
              className="month-grid__free"
              aria-hidden="true"
              onClick={() => onQuickAdd(day.iso)}
            />
          </div>
        );
      })}
    </div>
  );
}
